import { sort_center_api } from '@shared/api';
import { PageList } from '@shared/types/filter';
import { ParcelDto } from '@shared/types/parcel';
import { makeQueryString } from '@shared/utils/pagging';
import { Filter } from '@uni-space/uni-components';
import { CancellationReasonType, CancelParcelWithPhotoResponse, GetParcelPhotosResponse } from '../model/parcel';

export async function getAllParcels(
  page: number | undefined,
  pageSize: number | undefined,
  filters: Filter[] | undefined
): Promise<PageList<ParcelDto>> {
  const url: string = `v1/parcels?` + makeQueryString(page, pageSize, filters);
  const result = await sort_center_api<PageList<ParcelDto>>(url);
  return result.data;
}

/**
 * Сколько трек-номеров уходит в одном запросе. Фильтр передаётся в query string
 * (Filters[n].value через пробел), а Kestrel ограничивает строку запроса ~8 КБ.
 */
const TRACK_NUMBERS_CHUNK_SIZE = 50;

/**
 * Ищет посылки по списку трек-номеров через обычный список с фильтром trackNumbers.
 * Возвращает только найденные; сопоставлять с запрошенными нужно на клиенте по trackNumber.
 */
export async function findParcelsByTrackNumbers(trackNumbers: string[]): Promise<ParcelDto[]> {
  const found: ParcelDto[] = [];

  for (let i = 0; i < trackNumbers.length; i += TRACK_NUMBERS_CHUNK_SIZE) {
    const chunk = trackNumbers.slice(i, i + TRACK_NUMBERS_CHUNK_SIZE);
    // При одном треке бэкенд ищет по StartsWith, поэтому размер страницы берём с запасом —
    // точное совпадение всё равно отбирается на клиенте.
    const page = await getAllParcels(0, Math.max(chunk.length, TRACK_NUMBERS_CHUNK_SIZE), [
      { name: 'trackNumbers', value: chunk.join(' ') },
    ]);
    found.push(...page.items);
  }

  return found;
}

export async function cancelParcel(
  trackNumber: string,
  reason: CancellationReasonType,
  message: string
): Promise<void> {
  const url: string = `v1/parcels/${trackNumber}/cancel`;
  const request = {
    cancellationReasonType: reason,
    message: message,
  };
  await sort_center_api.post(url, request);
}

/** Все фото посылки (приёмка, отмена) с presigned-ссылками, от старых к новым. */
export async function getParcelPhotos(trackNumber: string): Promise<GetParcelPhotosResponse> {
  const url: string = `v1/parcels/${trackNumber}/photos`;
  const result = await sort_center_api<GetParcelPhotosResponse>(url);
  return result.data;
}

/**
 * Фото-фиксация отмены: снимок уходит multipart'ом, шлюз передаёт его в Media (тип «отмена»).
 * Причина отмены не передаётся — посылку отменяет отдельный `cancelParcel` сразу по скану, поэтому
 * к моменту снимка она уже отменена. Content-Type здесь не задаётся намеренно: интерсептор
 * `sort_center_api` пропускает FormData (иначе axios свернул бы её в JSON), и заголовок с boundary
 * подставляет браузер.
 */
export async function uploadCancellationPhoto(
  trackNumber: string,
  photo: Blob,
): Promise<CancelParcelWithPhotoResponse> {
  const form = new FormData();
  form.append('image', photo, `${trackNumber}.png`);

  const url: string = `v1/parcels/${trackNumber}/cancellation-photo`;
  const result = await sort_center_api.post<CancelParcelWithPhotoResponse>(url, form);
  return result.data;
}
